import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Table, Form, Alert, Spinner, Modal, Badge } from 'react-bootstrap'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface Priority {
  id: string
  name: string
  description?: string
  color: string
  level: number
  isActive: boolean
  createdAt: string
  _count?: {
    tickets: number
  }
}

interface PriorityData {
  name: string
  description: string
  color: string
  level: number
  isActive?: boolean
}

export const PrioritiesPage = () => {
  const { user } = useAuth() // eslint-disable-line @typescript-eslint/no-unused-vars
  const [priorities, setPriorities] = useState<Priority[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Check if user is admin
  if (user?.role !== 'ADMIN') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <i className="fas fa-lock text-muted" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3 text-muted">Access Denied</h5>
          <p className="text-muted">You need admin privileges to access priorities management</p>
        </div>
      </div>
    )
  }
  const [showModal, setShowModal] = useState(false)
  const [editingPriority, setEditingPriority] = useState<Priority | null>(null)
  const [priorityData, setPriorityData] = useState<PriorityData>({
    name: '',
    description: '',
    color: '#007bff',
    level: 1,
    isActive: true
  })
  const [submitting, setSubmitting] = useState(false)

  const colorOptions = [
    { value: '#dc3545', label: 'Red (Urgent)' },
    { value: '#fd7e14', label: 'Orange (High)' },
    { value: '#ffc107', label: 'Yellow (Medium)' },
    { value: '#28a745', label: 'Green (Low)' },
    { value: '#6c757d', label: 'Gray (Info)' }
  ]

  useEffect(() => {
    fetchPriorities()
  }, [])

  const fetchPriorities = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/priorities')
      setPriorities(response.data.data || [])
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Unauthorized: You need admin privileges to access priorities')
      } else if (err.response?.status === 403) {
        setError('Forbidden: You need admin privileges to access priorities')
      } else {
        setError('Failed to load priorities')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError('') // Clear previous errors
    try {
      if (editingPriority) {
        await api.patch(`/admin/priorities/${editingPriority.id}`, priorityData)
      } else {
        await api.post('/admin/priorities', priorityData)
      }
      // Only close modal and reset on success
      setShowModal(false)
      setEditingPriority(null)
      setPriorityData({ name: '', description: '', color: '#007bff', level: 1, isActive: true })
      fetchPriorities()
    } catch (err: any) {
      // Extract and display the error message
      const errorMessage = err.response?.data?.error?.message || 
                          err.response?.data?.message || 
                          'Failed to save priority'
      setError(errorMessage)
      // Don't close modal on error so user can see the message and fix it
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (priority: Priority) => {
    setError('') // Clear any previous errors
    setEditingPriority(priority)
    setPriorityData({
      name: priority.name,
      description: priority.description || '',
      color: priority.color,
      level: priority.level,
      isActive: priority.isActive
    })
    setShowModal(true)
  }

  const handleDelete = async (priorityId: string) => {
    if (window.confirm('Are you sure you want to delete this priority?')) {
      try {
        await api.delete(`/admin/priorities/${priorityId}`)
        fetchPriorities()
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete priority')
      }
    }
  }

  const handleToggleStatus = async (priorityId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/priorities/${priorityId}`, { isActive: !isActive })
      fetchPriorities()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update priority')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="priorities-page">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">Priorities Management</h2>
          <p className="text-muted mb-0 small">Define ticket priority levels and escalation rules</p>
        </div>
        <Button variant="primary" onClick={() => {
          setError('') // Clear any previous errors
          setShowModal(true)
        }} className="d-flex align-items-center">
          <i className="fas fa-plus me-2"></i>
          Add Priority
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-primary">{priorities.length}</h3>
              <p className="text-muted mb-0">Total Priorities</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-success">{priorities.filter(p => p.isActive).length}</h3>
              <p className="text-muted mb-0">Active Priorities</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{priorities.reduce((sum, p) => sum + (p._count?.tickets || 0), 0)}</h3>
              <p className="text-muted mb-0">Total Tickets</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-warning">
                {priorities.length > 0 ? Math.max(...priorities.map(p => p.level)) : 0}
              </h3>
              <p className="text-muted mb-0">Highest Level</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Priorities Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-exclamation-triangle me-2 text-primary"></i>
            Priorities ({priorities.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {priorities.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Priority</th>
                  <th>Level</th>
                  <th>Color</th>
                  <th>Tickets</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {priorities.sort((a, b) => b.level - a.level).map((priority) => (
                  <tr key={priority.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded-circle me-2" 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: priority.color 
                          }}
                        ></div>
                        <div>
                          <div className="fw-medium">{priority.name}</div>
                          {priority.description && (
                            <small className="text-muted">{priority.description}</small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">{priority.level}</Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded me-2" 
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            backgroundColor: priority.color 
                          }}
                        ></div>
                        <span className="text-muted">{priority.color}</span>
                      </div>
                    </td>
                    <td>
                      <Badge bg="info">{priority._count?.tickets || 0}</Badge>
                    </td>
                    <td>
                      <Badge bg={priority.isActive ? 'success' : 'danger'}>
                        {priority.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatDate(priority.createdAt)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEdit(priority)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant={priority.isActive ? "outline-warning" : "outline-success"} 
                          size="sm"
                          onClick={() => handleToggleStatus(priority.id, priority.isActive)}
                        >
                          <i className={`fas fa-${priority.isActive ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(priority.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-exclamation-triangle text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No priorities found</h5>
              <p className="text-muted">Create your first priority level using the button above</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Priority Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false)
        setEditingPriority(null)
        setPriorityData({ name: '', description: '', color: '#007bff', level: 1, isActive: true })
        setError('') // Clear error when closing
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPriority ? 'Edit Priority' : 'Add New Priority'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">
              <Alert.Heading className="h6">Error</Alert.Heading>
              {error}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Priority Name</Form.Label>
              <Form.Control
                type="text"
                value={priorityData.name}
                onChange={(e) => setPriorityData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Urgent, High, Medium, Low"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={priorityData.description}
                onChange={(e) => setPriorityData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe when this priority should be used..."
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Level (1-10)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="10"
                    value={priorityData.level}
                    onChange={(e) => setPriorityData(prev => ({ ...prev, level: parseInt(e.target.value) }))}
                    required
                  />
                  <Form.Text className="text-muted d-block">
                    Higher numbers = higher priority
                  </Form.Text>
                  {priorities.length > 0 && (
                    <Form.Text className="text-info d-block mt-1">
                      <i className="fas fa-info-circle me-1"></i>
                      Levels in use: {priorities.filter(p => !editingPriority || p.id !== editingPriority.id).map(p => `${p.level} (${p.name})`).join(', ')}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Color</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        className={`btn ${priorityData.color === color.value ? 'btn-primary' : 'btn-outline-secondary'} btn-sm`}
                        onClick={() => setPriorityData(prev => ({ ...prev, color: color.value }))}
                        style={{ minWidth: '80px' }}
                      >
                        <div 
                          className="rounded-circle d-inline-block me-2" 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: color.value 
                          }}
                        ></div>
                        {color.label}
                      </button>
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false)
            setEditingPriority(null)
            setPriorityData({ name: '', description: '', color: '#007bff', level: 1, isActive: true })
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={submitting || !priorityData.name.trim()}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editingPriority ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {editingPriority ? 'Update Priority' : 'Create Priority'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

